import { Provider, SearchApi, SearchTablesApi } from '@repo/api-gw-sdk';
import type {
  EmailSearchFilter,
  SearchFilesResponse,
  Configuration,
  Condition,
} from '@repo/api-gw-sdk';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/search';

export const search = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const searchApi = new SearchApi(config);
  const queryDbApi = new SearchTablesApi(config);

  return {
    // Microsoft 365 item search over the backup index (ADX): scoped by an index
    // path prefix (a user's OneDrive/Exchange namespace, a SharePoint site).
    // mode 'email' matches the term against from/to/subject header attributes
    // (Exchange message paths are opaque ids, so path search never matches them).
    m365Items: {
      useSearch: (
        term: string,
        pathPrefix: string | undefined,
        // Pins hits to one snapshot's indexed rows, so results match the
        // snapshot the explorer is browsing (and restore targets).
        snapshotId: string | undefined,
        mode: 'path' | 'email',
        limit: number,
        // Skip the request entirely (null SWR key) until a term is submitted.
        enabled = true,
        pageToken?: string,
        queryExecutionId?: string
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // No results found, not an error.
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          enabled ? baseUrl : null,
          'm365Items',
          currentProjectId,
          {
            cloudProvider: Provider.Microsoft365,
            query: mode === 'path' ? term : '',
            emailSearchFilter: mode === 'email' ? { text: [term] } : undefined,
            pathPrefix,
            snapshotId,
            limit,
            pageToken,
          }
        ),
    },
    // Google Workspace Gmail search over the backup index (BigQuery), scoped to
    // one mailbox path prefix. A message path is an opaque id, so the term is
    // matched against the indexed subject attribute rather than the path.
    gwsMails: {
      useSearch: (
        term: string,
        pathPrefix: string | undefined,
        // Pins hits to one snapshot's indexed rows, so results match the
        // snapshot the explorer is browsing (and restore targets).
        snapshotId: string | undefined,
        limit: number,
        // Skip the request entirely (null SWR key) until a term is submitted.
        enabled = true,
        pageToken?: string,
        queryExecutionId?: string
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // No results found, not an error.
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          enabled ? baseUrl : null,
          'gwsMails',
          currentProjectId,
          {
            cloudProvider: Provider.GoogleWorkspace,
            query: '',
            emailSearchFilter: { subject: [term] },
            pathPrefix,
            snapshotId,
            limit,
            pageToken,
          }
        ),
    },
    files: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        filters?: Condition,
        startDate?: Date,
        endDate?: Date,
        emailSearchFilter?: EmailSearchFilter
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // nothing - no results found, not an error
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          baseUrl,
          'files',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            filters: filters ? toJsonText(filters) : undefined,
            startDate,
            endDate,
            emailSearchFilter,
          }
        ),
    },
    // GWS Users search reuses the file search endpoint in directory-entity mode.
    users: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        snapshotId?: string,
        // Skip the request entirely (null SWR key) when disabled, so callers can
        // mount the hook unconditionally and only fetch once a term is submitted.
        enabled = true,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // No results found, not an error.
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          enabled ? baseUrl : null,
          'users',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            directorySearchFilter: { users: true, snapshotId },
          }
        ),
    },
    // GWS Shared Drives search reuses the file search endpoint in directory-entity mode.
    sharedDrives: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        snapshotId?: string,
        // Skip the request entirely (null SWR key) when disabled, so callers can
        // mount the hook unconditionally and only fetch once a term is submitted.
        enabled = true,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // No results found, not an error.
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          enabled ? baseUrl : null,
          'sharedDrives',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            directorySearchFilter: { sharedDrives: true, snapshotId },
          }
        ),
    },
    // GWS Groups search reuses the file search endpoint in directory-entity mode.
    groups: {
      useExecute: (
        term: string,
        limit: number,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        startDate?: Date,
        endDate?: Date
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof searchApi.searchFiles>) =>
            searchApi
              .searchFiles(args[0], {
                ...args[1],
                queryExecutionId,
              })
              .then(
                (response) => ({
                  ...response,
                  data: response.data.map((file) => ({
                    ...file,
                    id: file.resourceId + file.path,
                  })),
                }),
                (error) => {
                  if (error.code === 404) {
                    // No results found, not an error.
                    return {
                      data: [],
                      pagination: {
                        recordsCount: 0,
                        nextToken: undefined,
                        queryExecutionId: undefined,
                      },
                    } satisfies SearchFilesResponse;
                  }
                  throw error;
                }
              ),
          baseUrl,
          'groups',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            limit,
            pageToken,
            startDate,
            endDate,
            directorySearchFilter: { groups: true },
          }
        ),
    },
    dbs: {
      useExecute: (
        term: string,
        cloudProvider: Provider,
        queryExecutionId?: string,
        pageToken?: string,
        pageIndex?: number,
        pageSize?: number
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof queryDbApi.searchTables>) =>
            queryDbApi.searchTables(
              args[0],
              {
                ...args[1],
                queryExecutionId,
              },
              args[2],
              args[3]
            ),
          baseUrl,
          'db',
          currentProjectId,
          {
            cloudProvider,
            query: term,
            pageToken,
          },
          pageIndex,
          pageSize
        ),
    },
  };
};
